import type {
  MultiplayerGame,
  GameState,
  PlayerId,
  GameType,
  Settings,
  Side,
  GameStatus,
  MoveDirection,
  PlayerInfo
} from "src/core/types";

/**
 * Serializes game state for Firebase storage
 * Converts arrays and complex types to Firebase-compatible format
 */
export const serializeGameState = (gameState: GameState): { "0": number[]; "1": number[] } => {
  return {
    "0": gameState[0],
    "1": gameState[1]
  };
};

/**
 * Deserializes game state from Firebase
 */
export const deserializeGameState = (data: { "0": number[]; "1": number[] }): GameState => {
  return [data["0"], data["1"]];
};

/** Shape of a multiplayer game as stored in Firebase */
type FirebaseGameData = {
  gameId: string;
  roomCode: string;
  host: PlayerId;
  guest: PlayerId | null;
  settings: Settings;
  gameType: GameType;
  gameState: { "0": number[]; "1": number[] };
  moveHistory?: Array<{
    playerId: PlayerId;
    index: number;
    direction: MoveDirection;
    side: Side;
    timestamp: number;
  }>;
  currentPlayer: Side;
  status: GameStatus;
  players?: Record<PlayerId, PlayerInfo>;
  createdAt: number;
  updatedAt: number;
};

/**
 * Serializes a full multiplayer game for Firebase
 */
export const serializeMultiplayerGame = (game: MultiplayerGame): FirebaseGameData => {
  return {
    gameId: game.gameId,
    roomCode: game.roomCode,
    host: game.host,
    guest: game.guest,
    settings: game.settings,
    gameType: game.gameType,
    gameState: serializeGameState(game.gameState),
    moveHistory: game.moveHistory,
    currentPlayer: game.currentPlayer,
    status: game.status,
    players: game.players,
    createdAt: game.createdAt,
    updatedAt: game.updatedAt
  };
};

/**
 * Deserializes a multiplayer game from Firebase
 */
export const deserializeMultiplayerGame = (data: FirebaseGameData): MultiplayerGame => {
  return {
    gameId: data.gameId,
    roomCode: data.roomCode,
    host: data.host,
    guest: data.guest || null,
    settings: data.settings,
    gameType: data.gameType,
    gameState: deserializeGameState(data.gameState),
    moveHistory: data.moveHistory || [],
    currentPlayer: data.currentPlayer,
    status: data.status,
    players: data.players || {},
    createdAt: data.createdAt,
    updatedAt: data.updatedAt
  };
};

/**
 * Creates an initial multiplayer game object
 */
export const createInitialMultiplayerGame = (
  gameId: string,
  roomCode: string,
  hostId: PlayerId,
  gameState: GameState,
  settings: Settings,
  gameType: GameType
): MultiplayerGame => {
  const now = Date.now();
  return {
    gameId,
    roomCode,
    host: hostId,
    guest: null,
    settings,
    gameType,
    gameState,
    moveHistory: [],
    currentPlayer: settings.startingSide,
    status: "waiting",
    players: {
      [hostId]: {
        playerId: hostId,
        side: 0,
        connected: true,
        lastSeen: now
      }
    },
    createdAt: now,
    updatedAt: now
  };
};
